import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import PositionLedger, { DetailValue, BreakdownRow } from './PositionLedger.jsx'
import PrivateValue from '../PrivateValue.jsx'
import AccountName from '../AccountName.jsx'
import { usePositionHistory } from '../../hooks/usePositionHistory.js'
import { summarizePositionHistory } from '../../lib/positionHistory.js'
import './positionLedgerPager.css'

const money = value => value == null ? '—' : Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
const signed = value => value == null ? '—' : `${value > 0 ? '+' : ''}${money(value)}`
const color = value => value > 0 ? 'text-forest' : value < 0 ? 'text-ember' : 'text-inkMuted'

const CurrentPosition = memo(PositionLedger)

const HistoryPage = memo(function HistoryPage({ history }) {
  const [year, setYear] = useState('all')
  const summary = useMemo(() => summarizePositionHistory(history.rows, year), [history.rows, year])
  if (history.status !== 'ready') return <div className="p-3 text-xs text-inkMuted" role="status">
    {history.status === 'loading' ? 'Loading history…' : 'History unavailable.'}
    {history.status === 'error' && <button type="button" onClick={history.retry} className="ml-2 underline">Retry</button>}
  </div>
  return <div className="p-3 text-xs" data-testid="position-history">
    <div className="flex justify-between items-center gap-3 mb-5 text-inkMuted">
      <span>Realized P&L</span>
      <select aria-label="History year" value={year} onChange={event => setYear(event.target.value)}
        title="Closed trades use their final exit year; partial exits use their execution year. This is recorded trade history, not a tax-year report."
        className="bg-canvas border border-border rounded px-2 py-1 text-xs max-w-[50%]">
        <option value="all">All time</option>{summary.years.map(value => <option key={value} value={value}>{value}</option>)}
      </select>
    </div>
    <dl className="flex flex-col gap-4">
      {[['Stock P&L', summary.stock, 'stock'], ['Options P&L', summary.options, 'options'], ['Total realized P&L', summary.total, null]].map(([label, value, kind]) => {
        const records = summary.rows.filter(row => !kind || row.kind === kind)
        const complete = field => records.every(row => row[field] != null) ? records.reduce((sum, row) => sum + Math.round(row[field] * 100), 0) / 100 : null
        return <div key={label} className={`flex items-baseline justify-between gap-3 ${!kind ? 'pt-4 border-t border-border' : ''}`}>
          <dt className={kind ? 'text-inkMuted' : ''}>{label}</dt>
          <dd><DetailValue label={label} value={signed(value)} colorClass={color(value)}>
            <div className="text-inkMuted mb-1">{label}</div>
            <BreakdownRow label="Gross realized P&L" value={signed(complete('gross'))} />
            <BreakdownRow label="Commission and fees" value={money(complete('costs'))} />
            <BreakdownRow label="Net realized P&L" value={signed(value)} colorClass={color(value)} />
          </DetailValue></dd>
        </div>
      })}
    </dl>
    <div className="mt-3 text-[11px] text-inkMuted">All accounts · Recorded history</div>
    {summary.unavailable > 0 && <div className="mt-3 text-xs text-inkMuted" role="status">Some realized results or exit dates are unavailable. Affected totals show —.</div>}
    <h3 className="text-xs font-normal text-inkMuted mt-6 mb-2">Realized trades · {summary.rows.length}</h3>
    {summary.rows.length === 0 && <p className="text-inkMuted py-2">No recorded realized trades{year === 'all' ? '.' : ' in this year.'}</p>}
    {summary.rows.map(row => <details key={row.id} className="position-history-trade border-t border-border py-3">
      <summary className="flex justify-between items-start gap-3 cursor-pointer">
        <span className="min-w-0"><span className="position-history-name break-words">{row.name}{row.quantity != null && <> · {row.quantity.toLocaleString('en-US', { maximumFractionDigits: 4 })} {row.kind === 'options' ? (row.quantity === 1 ? 'contract' : 'contracts') : 'shares'}</>}</span>
          <span className="block mt-1 ml-3 text-inkMuted text-[11px]">{row.date || 'Exit date unavailable'}</span></span>
        <span className={`shrink-0 tabular-nums ${color(row.net)}`}><PrivateValue>{signed(row.net)}</PrivateValue></span>
      </summary>
      <div className="pt-3 pl-3">
        <BreakdownRow label="Account" value={<AccountName accountNumber={row.account} />} />
        <BreakdownRow label="Status" value={row.status} />
        <BreakdownRow label="Entry date" value={row.entryDate || '—'} />
        <BreakdownRow label="Avg entry" value={money(row.entryPrice)} />
        <BreakdownRow label="Avg exit" value={money(row.exitPrice)} />
        <BreakdownRow label="Gross realized P&L" value={signed(row.gross)} />
        <BreakdownRow label="Commission and fees" value={money(row.costs)} />
        <BreakdownRow label="Net realized P&L" value={signed(row.net)} colorClass={color(row.net)} />
        <p className="mt-2 text-[11px] text-inkMuted">{row.accounting}</p>
      </div>
    </details>)}
  </div>
})

export function PositionLedgerPages({ symbol, stage, live, loading, history }) {
  const [page, setPage] = useState(['closed', 'reviewed'].includes(stage) ? 1 : 0)
  const viewport = useRef(null)
  const wheelGesture = useRef({ time: 0, distance: 0, direction: 0 })
  const touch = useRef(null)
  // Existing hover portals dismiss on scroll; changing pages must dismiss them
  // too, including when an inert panel previously held keyboard focus.
  useEffect(() => { viewport.current?.dispatchEvent(new Event('scroll', { bubbles: true })) }, [page])
  useEffect(() => {
    const element = viewport.current
    const wheel = event => {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY) || Math.abs(event.deltaX) < 2) return
      event.preventDefault()
      const now = performance.now()
      const direction = Math.sign(event.deltaX)
      const gesture = wheelGesture.current
      // Trackpads send small deltas. Accumulate them, and respond to a deliberate
      // reversal immediately instead of blocking input for half a second.
      if (now - gesture.time > 120 || direction !== gesture.direction) gesture.distance = 0
      gesture.time = now
      gesture.direction = direction
      gesture.distance += Math.abs(event.deltaX) * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? element.clientWidth : 1)
      if (gesture.distance < 12) return
      setPage(direction > 0 ? 1 : 0)
    }
    element.addEventListener('wheel', wheel, { passive: false })
    return () => element.removeEventListener('wheel', wheel)
  }, [])
  return <section aria-label="Position information" className="position-ledger-pager font-sans text-xs text-ink">
    <div className="position-ledger-heading text-inkMuted">
      <span aria-live="polite">{page === 0 ? 'Position' : 'History'}</span>
      <div className="position-ledger-dots" aria-label="Ledger pages">
        {['Position', 'History'].map((label, index) => <button key={label} type="button" aria-label={`${label} page`} aria-pressed={page === index} onClick={() => setPage(index)}><span className={page === index ? 'bg-ink' : 'bg-inkMuted/40'} /></button>)}
      </div>
      <div className="flex justify-end">
        <button type="button" aria-label="Previous ledger page" className="position-ledger-arrow" disabled={page === 0} onClick={() => setPage(0)}><ChevronLeft size={13} /></button>
        <button type="button" aria-label="Next ledger page" className="position-ledger-arrow" disabled={page === 1} onClick={() => setPage(1)}><ChevronRight size={13} /></button>
      </div>
    </div>
    <div ref={viewport} className="position-ledger-viewport" onTouchStart={event => { touch.current = { x: event.touches[0].clientX, y: event.touches[0].clientY } }}
      onTouchEnd={event => { const start = touch.current; const end = event.changedTouches[0]; if (start && Math.abs(end.clientX - start.x) > 50 && Math.abs(end.clientX - start.x) > Math.abs(end.clientY - start.y)) setPage(end.clientX < start.x ? 1 : 0); touch.current = null }}>
      <div className="position-ledger-track" style={{ transform: `translate3d(-${page * 50}%, 0, 0)` }}>
        <div className="position-ledger-page" inert={page !== 0 ? '' : undefined} aria-hidden={page !== 0}>
          {stage === 'active' || live || loading ? <CurrentPosition symbol={symbol} live={live} loading={loading} /> : <div className="p-3"><div className="flex justify-between"><span className="text-inkMuted">Qty</span><span>{['closed', 'reviewed'].includes(stage) ? '0' : '—'}</span></div><p className="mt-5 text-inkMuted">No open position</p></div>}
        </div>
        <div className="position-ledger-page" inert={page !== 1 ? '' : undefined} aria-hidden={page !== 1}><HistoryPage history={history} /></div>
      </div>
    </div>
  </section>
}

export default function PositionLedgerPager({ userId, symbol, stage, live, loading, refreshVersion }) {
  const history = usePositionHistory(userId, symbol, refreshVersion)
  return <PositionLedgerPages key={`${userId}:${symbol}:${stage}`} symbol={symbol} stage={stage} live={live} loading={loading} history={history} />
}
